import { CognitoJwtVerifier } from 'aws-jwt-verify';
import { AuthError } from '../types/auth';
// Cache for JWT verifiers to avoid recreating them
const verifierCache = new Map();
/**
 * Get or create a JWT verifier for the given configuration
 */
function getVerifier(config, tokenUse = 'access') {
    const cacheKey = `${config.userPoolId}-${config.clientId}-${tokenUse}`;
    if (!verifierCache.has(cacheKey)) {
        const verifier = CognitoJwtVerifier.create({
            userPoolId: config.userPoolId,
            tokenUse,
            clientId: config.clientId,
        });
        verifierCache.set(cacheKey, verifier);
    }
    return verifierCache.get(cacheKey);
}
/**
 * Decode a JWT token with full validation
 * @param token JWT token string
 * @param config Validation configuration
 * @param tokenUse Token type ('access' or 'id')
 * @returns Decoded and validated token claims
 */
export async function decodeToken(token, config, tokenUse = 'access') {
    try {
        const verifier = getVerifier(config, tokenUse);
        const payload = await verifier.verify(token);
        return payload;
    }
    catch (error) {
        throw new AuthError('INVALID_TOKEN', 'Token validation failed', error);
    }
}
/**
 * Check if a token is expired with validation
 * @param token JWT token string
 * @param config Validation configuration
 * @param bufferSeconds Optional buffer time in seconds (default: 30)
 * @param tokenUse Token type ('access' or 'id')
 * @returns True if token is expired or will expire within buffer time
 */
export async function isTokenExpired(token, config, bufferSeconds = 30, tokenUse = 'access') {
    try {
        const claims = await decodeToken(token, config, tokenUse);
        const now = Math.floor(Date.now() / 1000);
        return claims.exp <= now + bufferSeconds;
    }
    catch {
        return true; // Consider invalid tokens as expired
    }
}
/**
 * Get the expiration time of a token
 * @param token JWT token string
 * @param config Validation configuration
 * @param tokenUse Token type ('access' or 'id')
 * @returns Expiration time as Date object
 */
export async function getTokenExpiration(token, config, tokenUse = 'access') {
    const claims = await decodeToken(token, config, tokenUse);
    return new Date(claims.exp * 1000);
}
/**
 * Get the time until token expiration
 * @param token JWT token string
 * @param config Validation configuration
 * @param tokenUse Token type ('access' or 'id')
 * @returns Seconds until expiration (negative if already expired)
 */
export async function getTimeUntilExpiration(token, config, tokenUse = 'access') {
    const claims = await decodeToken(token, config, tokenUse);
    const now = Math.floor(Date.now() / 1000);
    return claims.exp - now;
}
/**
 * Extract user information from token claims
 * @param claims Token claims
 * @returns User information object
 */
export function extractUserInfo(claims) {
    const userInfo = {
        userId: claims['custom:userId'],
        customerId: claims['custom:customerId'],
        market: claims['custom:market'],
        brand: claims['custom:brand'],
        // Additional fields; always defined by cognito, may not be defined in host seamless auth
        email: claims.email,
        firstName: claims.given_name,
        lastName: claims.family_name,
        // Legacy fields for backwards compatibility
        username: claims.sub,
        attributes: {},
    };
    return userInfo;
}
/**
 * Validate token structure and basic claims
 * @param token JWT token string
 * @param config Validation configuration
 * @param tokenUse Token type ('access' or 'id')
 * @returns True if token has valid structure and required claims
 */
export async function validateTokenStructure(token, config, tokenUse = 'access') {
    try {
        const claims = await decodeToken(token, config, tokenUse);
        return !!(claims.sub && claims.exp && claims.iat && claims.aud);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=token.js.map